// ResponsiveContext.js
import { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react'

const ResponsiveContext = createContext(null)

const DEFAULT_BREAKPOINTS = {
  xs: 640,
  sm: 768,
  md: 1050,
  lg: 1250,
  xl: 1551
}

// Get initial state from all available sources
const getInitialState = () => {
  // Check for client-side storage first (most accurate for refreshes)
  if (typeof window !== 'undefined') {
    try {
      // Try session storage first (persists across refreshes)
      const cached = sessionStorage.getItem('__VIEWPORT_STATE')
      if (cached) {
        return JSON.parse(cached)
      }

      // Then check global set by script
      if (window.__REAL_VIEWPORT_STATE) {
        return window.__REAL_VIEWPORT_STATE
      }
    } catch (e) {
      // Storage access might fail
    }
  }

  // For SSR or if no client info is available, return default state
  return {
    isMobile: true,
    isTablet: true,
    isDesktop: true,
    isSmallMobile: true,
    isLargeDesktop: true
  }
}

export const useResponsive = () => {
  const context = useContext(ResponsiveContext)
  if (!context) {
    throw new Error('useResponsive must be used within a ResponsiveProvider')
  }
  return context
}

export const ResponsiveProvider = ({
  children,
  initialState,
  breakpoints = DEFAULT_BREAKPOINTS
}) => {
  // Use a ref to track if we've updated after initial render
  const hasUpdatedRef = useRef(false)

  // Use cached state or initialState or default
  const [state, setState] = useState(() => {
    // Try to get cached state from session storage or global
    const cachedState = getInitialState()

    // If we have initialState from server, use it as a fallback
    return (
      cachedState ||
      initialState || {
        isMobile: true,
        isTablet: true,
        isDesktop: true,
        isSmallMobile: true,
        isLargeDesktop: true
      }
    )
  })

  // Convert breakpoints to numbers (memoized to prevent infinite loops)
  const breakpointValues = useMemo(
    () =>
      Object.entries(breakpoints).reduce(
        (acc, [key, value]) => {
          acc[key] =
            typeof value === 'string' ? parseInt(value.replace('px', '')) : value
          return acc
        },
        {}
      ),
    [breakpoints]
  )

  // Update on resize and save to session storage
  useEffect(() => {
    const updateResponsiveState = () => {
      // Skip the first update if we already have real state
      if (
        !hasUpdatedRef.current &&
        typeof window !== 'undefined' &&
        window.__REAL_VIEWPORT_STATE
      ) {
        hasUpdatedRef.current = true
        return
      }

      const width = window.innerWidth

      const newState = {
        isMobile: width <= breakpointValues.sm,
        isTablet: width <= breakpointValues.md,
        isDesktop: width <= breakpointValues.lg,
        isSmallMobile: width <= breakpointValues.xs,
        isLargeDesktop: width <= breakpointValues.xl
      }

      // Only update if values actually changed
      setState((prevState) => {
        if (JSON.stringify(prevState) === JSON.stringify(newState)) {
          return prevState // No change needed
        }
        return newState
      })

      // Update HTML classes
      document.documentElement.classList.toggle('is-mobile', newState.isMobile)
      document.documentElement.classList.toggle('is-tablet', newState.isTablet)
      document.documentElement.classList.toggle(
        'is-desktop',
        newState.isDesktop
      )

      // Update data attributes
      document.documentElement.dataset.isMobile = newState.isMobile
      document.documentElement.dataset.isTablet = newState.isTablet
      document.documentElement.dataset.isDesktop = newState.isDesktop

      // Save to session storage
      try {
        sessionStorage.setItem('__VIEWPORT_STATE', JSON.stringify(newState))
      } catch (e) {
        // Session storage not available
      }

      // Mark as updated
      hasUpdatedRef.current = true
    }

    // Set up resize handler
    let timeoutId
    const debouncedResize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(updateResponsiveState, 100)
    }

    window.addEventListener('resize', debouncedResize)

    // Run once on mount to ensure we're in sync
    if (!hasUpdatedRef.current) {
      updateResponsiveState()
    }

    return () => {
      window.removeEventListener('resize', debouncedResize)
      clearTimeout(timeoutId)
    }
  }, [breakpointValues])

  return (
    <ResponsiveContext.Provider value={state}>
      {children}
    </ResponsiveContext.Provider>
  )
}
